import { ViewModel } from "binder";
import { BooleanColumnViewModel, DateColumnViewModel, EnumColumnViewModel, NumericColumnViewModel, PaginationInfo, StringColumnViewModel } from "../..";
import { getAsBoolean } from "../utilities";
import { ColumnViewModel } from "./column/column-viewModel";
import { IColumnVisitor } from "./column/columnVisitor";
import { BooleanFilterViewModel } from "./columnFilter/booleanFilter/booleanFilter-viewModel";
import { DateFilterViewModel } from "./columnFilter/dateFilter/dateFilter-viewModel";
import { EnumFilterViewModel } from "./columnFilter/enumFilter/enumFilter-viewModel";
import { NumericFilterViewModel } from "./columnFilter/numericFilter/numericFilter-viewModel";
import { StringFilterViewModel } from "./columnFilter/stringFilter/stringFilter-viewModel";
import { ExTableViewModel } from "./exTable-viewModel";


export class ServerFilteringTable extends ExTableViewModel{
    
    private pageNo:number;
    constructor(parent:ViewModel) {
        super(parent)
        
        this.pageNo=1;
        this.pagesize = 15;
    }

    public set dataloader(v: ServerSideDataLoader) { 
        this._dataloader = v; 
        v.table = this; 
        if(v.paginationInfo!=null){
            this.updatePaginationInfo(v.paginationInfo);
        }
    } public get dataloader(): ServerSideDataLoader { return this._dataloader;} private _dataloader: ServerSideDataLoader;
    public set enableelasticsearch(v: boolean) {
        this._enableelasticsearch = getAsBoolean(v);
        this.shallshowfullsearch = this._enableelasticsearch;
    } public get enableelasticsearch(): boolean { return this._enableelasticsearch;} private _enableelasticsearch: boolean;

    sortChanged() {
        this.requestRefresh();
    }
    filterChanged() {
        this.requestRefresh();
    }
    applyFullSearch() {
        this.requestRefresh();
    }

    async informPageChange(pageNo: number, pageSize: number): Promise<PaginationInfo> {
        this.pageNo = pageNo;
        this.pagesize = pageSize;
        this.onPageChanged();
        return this.requestRefresh();
    }

    private async requestRefresh(): Promise<PaginationInfo>{
        let filteringObject = this.createFilterObject()
        let result = await this.dataloader.run(filteringObject);
        this.updatePaginationInfo(result);
        return result;
    }

    private createFilterObject():FilteringInformation{
        if (this.enableelasticsearch == true){
            let filterCreator = new ElasticSearchFilterCreator(this.pageNo, this.pagesize);
            return filterCreator.createFilter(this.columns,this.fullsearchstring);
        }else{
            let filterCreator = new TraditionalFilterCreator(this.pageNo, this.pagesize);
            return filterCreator.createFilter(this.columns);
        }
    }
}

export class TraditionalFilterCreator{
    private pageNo:number;
    private pageSize:number;

    constructor(pageNo:number, pageSize:number) {
        this.pageNo = pageNo;
        this.pageSize = pageSize;    
    }

    public createFilter(columns):FilteringInformation{
        let filter = new FilteringExpressionBuilder().getFilteringExpression(columns);
        let order = new SortExpressionBuilder().getSortExpression(columns);
        let result = new FilteringInformation();
        result.pageNo = this.pageNo;
        result.pageSize = this.pageSize;
        result.filters = filter;
        result.orders = order;
        return result;
    }
}

export class ElasticSearchFilterCreator{
    private pageNo:number;
    private pageSize:number;
    constructor(pageNo:number, pageSize:number) {
        this.pageNo = pageNo;
        this.pageSize = pageSize; 
    }
    
    public createFilter(columns,fullsearchstring:string):FilteringInformation{
        let filter = new FilteringExpressionBuilderForElasticSearch().getFilteringExpression(columns);
        let order = new SortExpressionBuilderForElasticSearch().getSortExpression(columns);
        let result = new FilteringInformation();
        result.pageNo = this.pageNo;
        result.pageSize = this.pageSize;
        result.filters = filter;
        result.orders = order;
        result.fullSearchString = fullsearchstring;
        return result;
    }
}

type FilteringInformationToPagination = (filteringInformation: any) => Promise<PaginationInfo>;
type FilteringInformationAndExportInfoToPagination = (filteringInformation: any,exportType?:string) => Promise<PaginationInfo>;

export class ServerSideDataLoader{
    private loaderFunction : (filteringInformation:any,exportType?:string)=>Promise<PaginationInfo>;
    public set table(v: ServerFilteringTable) { 
        this._table = v; 
        v.pagesize = this.pageSize;
        if(this.refreshResult!=null){
            this.table.updatePaginationInfo(this.refreshResult);
        }
    } public get table(): ServerFilteringTable { return this._table;} private _table: ServerFilteringTable;
    
    private pageSize:number;
    constructor(loadingFunction:FilteringInformationToPagination | FilteringInformationAndExportInfoToPagination
                                ,pageSize?:number) {
        this.loaderFunction = loadingFunction;
        if(pageSize==null)
            pageSize = 15;
        this.pageSize = pageSize;
    }
    private lastUsedParameter:any;
    async runUnTyped(filteringInformation:any,exportType:string=null):Promise<PaginationInfo>{
        this.lastUsedParameter = filteringInformation;
        this.paginationInfo = await this.loaderFunction(filteringInformation,exportType);
        return this.paginationInfo;
    }
    async run(filteringInformation:FilteringInformation):Promise<PaginationInfo>{
        return this.runUnTyped({
                PaginationInfo:{
                    PageNo:filteringInformation.pageNo,
                    PageSize:filteringInformation.pageSize
                },
                OrderInfo:filteringInformation.orders,
                Filters:{
                    FilterString:filteringInformation.filters,
                    FullSearchString:filteringInformation.fullSearchString
                }
        });
    }

    private refreshResult : PaginationInfo;
    public paginationInfo : PaginationInfo;
    async refresh(){
       return this.internalRefresh(false,null);
    }
    refreshExport(type:string) {
        return this.internalRefresh(true,type);
    }
    async internalRefresh(isExport:boolean,exportType:string){
        let parameter = this.lastUsedParameter;
        if(parameter == null){
            parameter = {
                PaginationInfo:{
                    PageNo:1,
                    PageSize:this.pageSize
                },
                OrderInfo:[],
                Filters:{
                    FilterString:''
                }
            }
        }
        let result =await this.runUnTyped(parameter,exportType);
        if(result==ServerSideDataLoader.UnchangedPaginationInfo)
            return
        if(this.table==null){
            this.refreshResult = result;
        }else{
            this.table.updatePaginationInfo(result);
        }
    }

    public static UnchangedPaginationInfo : PaginationInfo = null;
}

export class FilteringInformation{
    public pageNo:number;
    public pageSize:number;
    public orders:{fieldName:string,order:string,type:string}[];
    public filters:string;
    public fullSearchString:string;
}

class SortExpressionBuilder {
    public getSortExpression(columns:ColumnViewModel[]){
        return columns.filter(x=>x.isNoSort==false).map(x=>{
            return {
                fieldName:x.fieldname,
                order:x.isSortAsc?'ASC':'DESC',
                type:''
            };
        })
    }

}
class SortExpressionBuilderForElasticSearch implements IColumnVisitor {
    visitBooleanColumn(column: BooleanColumnViewModel, parameter: any) {
        return {
            fieldName:column.fieldname,
            order:column.isSortAsc?'ASC':'DESC',
            type:'bool'
        };    
    }
    visitDateColumn(column: DateColumnViewModel, parameter: any) {
        return {
            fieldName:column.fieldname,
            order:column.isSortAsc?'ASC':'DESC',
            type:'date'
        };
    }
    visitEnumColumn(column: EnumColumnViewModel, parameter: any) {
        return {
            fieldName:column.fieldname,
            order:column.isSortAsc?'ASC':'DESC',
            type:'numeric'
        };
    }
    visitNumericColumn(column: NumericColumnViewModel, parameter: any) {
        return {
            fieldName:column.fieldname,
            order:column.isSortAsc?'ASC':'DESC',
            type:'numeric'
        };
    }
    visitStringColumn(column: StringColumnViewModel, parameter: any) {
        return {
            fieldName:column.fieldname,
            order:column.isSortAsc?'ASC':'DESC',
            type:'string'
        };
    }
    public getSortExpression(columns:ColumnViewModel[]){
        return columns.filter(x=>x.isNoSort==false).map(x=>{
            return x.accept(this,null);
        })
    }

}

class FilteringExpressionBuilder implements IColumnVisitor {
    
    private expressions :string[];
    public getFilteringExpression(columns:ColumnViewModel[]){
        this.expressions = [];
        columns.forEach(x=>x.accept(this,null));
        if(this.expressions.length==0)
            return "";
        else 
            return `x=>${this.expressions.join(' && ')}`;
    }
    visitBooleanColumn(column: BooleanColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let value = (<BooleanFilterViewModel>column.filter).value;
            if(value){
                this.expressions.push(`x.${column.fieldname}==true`);
            }else{
                this.expressions.push(`x.${column.fieldname}==false`);
            }
        }
    }
    visitDateColumn(column: DateColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let filter = (<DateFilterViewModel>column.filter);
            let largerThan = filter.largerThan;
            let smallerThan = filter.smallerThan;
            if(filter.applySmaller){
                this.expressions.push(`x.${column.fieldname}.ToUniversalTime()<=DateTime(${smallerThan.year()},${smallerThan.month()+1},${smallerThan.date()},23,59,59,DateTimeKind.Utc)`);
            }
            if(filter.applylarger){
                this.expressions.push(`x.${column.fieldname}.ToUniversalTime()>=DateTime(${largerThan.year()},${largerThan.month()+1},${largerThan.date()},0,0,0,DateTimeKind.Utc)`);
            }
        }
    }
    visitEnumColumn(column: EnumColumnViewModel, parameter: any) {
        if(column.filter.isActive){
            let filter = (<EnumFilterViewModel>column.filter);
            let fieldName = column.fieldname;
            let values =filter.enumValues.filter(x=>x.isSelected).map(x=>x.value);
            let parts = values.map(x=>`x.${fieldName}==${x}`).join(' || ');
            this.expressions.push(`(${parts})`)
        }   
    }
    visitNumericColumn(column: NumericColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let filter = (<NumericFilterViewModel>column.filter);
            let largerThan = filter.largerThan;
            let smallerThan = filter.smallerThan;
            if(filter.applySmaller){
                this.expressions.push(`x.${column.fieldname}<=${smallerThan}`);
            }
            if(filter.applylarger){
                this.expressions.push(`x.${column.fieldname}>=${largerThan}`);
            }
        }
    }
    visitStringColumn(column: StringColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let filter = (<StringFilterViewModel>column.filter);
            let value = filter.containsValue;
            if(value!=null && value.trim()!=''){
                this.expressions.push(`x.${column.fieldname}.Contains("${value}")`)
            }
        }
    }

}

class FilteringExpressionBuilderForElasticSearch implements IColumnVisitor {
    
    public getFilteringExpression(columns:ColumnViewModel[]){
        let filters = columns.map(x=>x.accept(this,null)).filter(x=>x!=null);
        return JSON.stringify(filters);
    }

    visitBooleanColumn(column: BooleanColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let value = (<BooleanFilterViewModel>column.filter).value;
            return {Type:"boolean", Name:column.fieldname, Value:value};
        }
        return null;
    }
    visitDateColumn(column: DateColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let filter = (<DateFilterViewModel>column.filter);
            let largerThan = filter.largerThan;
            let smallerThan = filter.smallerThan;
            return {Type:"date", Name:column.fieldname, Min:largerThan, Max:smallerThan};
        }
        return null;
    }
    visitEnumColumn(column: EnumColumnViewModel, parameter: any) {
        if(column.filter.isActive){
            let filter = (<EnumFilterViewModel>column.filter);
            let fieldName = column.fieldname;
            let values =filter.enumValues.filter(x=>x.isSelected).map(x=>x.value);
            return {Type:"enum", Name:fieldName, Values:values}
        }
        return null;
    }
    visitNumericColumn(column: NumericColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let filter = (<NumericFilterViewModel>column.filter);
            let largerThan = filter.largerThan;
            let smallerThan = filter.smallerThan;
            return {Type:"numeric", Name:column.fieldname, Min:largerThan, Max:smallerThan};
        }
        return null;
    }
    visitStringColumn(column: StringColumnViewModel, parameter: any) {
        if(column.isFiltered){
            let value = (<StringFilterViewModel>column.filter).containsValue;
            return {Type:"string", Name:column.fieldname, Value:value};
        }
        return null;
    }

}